/**
 * @module utils/constants/agentEnrichment
 *
 * Single source of truth for the agent contact-enrichment + discovery UI
 * (TD-2293): field labels, credit costs, and the tag/reply protocol shared
 * with the agent runtime.
 *
 * ## Reveal protocol — SHIPPED (TD-2281, td-agent/td-backend integration/sprint-39)
 *
 * Deterministic three-party design (ADR TD-2296): the model only calls
 * `quote_reveal` and stops; the td-agent Python harness emits the reveal tags
 * into the stream (`events/stream_mapper.py`) and — on the FE's hidden CONFIRM
 * — executes the reveal tool itself (`events/reveal_confirm.py`), bypassing
 * the model. The FE detects the tags and sends the hidden control replies
 * below into the SAME agent session that produced the quote.
 *
 *   agent→FE  [REVEAL_PENDING:<token>:<cost>:<remaining>]  quote issued (once
 *             per invocation; no tag when caps were exceeded / no token)
 *   agent→FE  [REVEAL_STARTED:<jobId>]                      reveal running (keep polling)
 *   agent→FE  [REVEAL_BUSY]                                 reveal poll still pending
 *   agent→FE  [REVEAL_RESULT]                               prepended to every TERMINAL
 *             reveal turn (values / bulk result / cancel ack / no-match)
 *   FE→agent  CONFIRM_REVEAL:<token> | CANCEL_REVEAL:<token>
 *   FE→agent  CHECK_REVEAL_RESULTS:<jobId>                  ~20s poll cadence
 *
 * The token is base64url + '.' (never contains ':'); token and jobId are NOT
 * UUIDs — do not UUID-guard them. Revealed values arrive as ordinary agent
 * markdown in the render turn, prefixed with [REVEAL_RESULT] (stripped for
 * display). TD-2442: the reveal loop keys ONLY on its own scoped markers
 * ([REVEAL_BUSY]/[REVEAL_RESULT], not the shared [BUSY]/tag-free) so it can't
 * cross-read the search poll loop, and vice versa.
 *
 * ## Options / discovery — PROPOSED (agent side unbuilt)
 *
 * `[AGENT_OPTIONS:...]` (TD-2362 agent contribution) and
 * `[DISCOVERY_RESULTS:...]` (TD-2292) have no agent-side implementation yet.
 * The shapes below are the FE proposal to reconcile with those tickets; the
 * FE keeps them behind AGENT_ENRICHMENT_ENABLED and renders nothing until the
 * agent emits them.
 *
 * Field naming (TD-2096): UI labels are "Work Email" / "Mobile Phone" —
 * label-only. API field keys stay `email` / `phone` (DB `personal_email` /
 * `mobile_phone`).
 */

import { EMAIL_COST, PHONE_COST } from '../../components/profiler/CompanyDetails/KeyContacts/keyContactsCostUtils';

export { EMAIL_COST, PHONE_COST };

/** API field keys — never renamed (TD-2096 is label-only). */
export const ENRICHMENT_FIELD = {
  EMAIL: 'email',
  PHONE: 'phone'
};

/** User-facing field labels (TD-2096). */
export const FIELD_LABEL = {
  [ENRICHMENT_FIELD.EMAIL]: 'Work Email',
  [ENRICHMENT_FIELD.PHONE]: 'Mobile Phone'
};

/** Per-field credit cost, keyed by API field. */
export const FIELD_COST = {
  [ENRICHMENT_FIELD.EMAIL]: EMAIL_COST,
  [ENRICHMENT_FIELD.PHONE]: PHONE_COST
};

/**
 * Agent → FE tag names.
 * REVEAL_* are SHIPPED marker tags with colon-separated scalar params (no
 * JSON body, no close tag). OPTIONS / DISCOVERY_RESULTS are PROPOSED
 * payload tags that wrap JSON between `[NAME:...]` and `[/NAME]`.
 */
export const ENRICHMENT_TAG = {
  // SHIPPED (TD-2281)
  REVEAL_PENDING: 'REVEAL_PENDING', // [REVEAL_PENDING:<token>:<cost>:<remaining>]
  REVEAL_STARTED: 'REVEAL_STARTED', // [REVEAL_STARTED:<jobId>]
  // PROPOSED (TD-2362 / TD-2292 — agent side unbuilt)
  OPTIONS: 'AGENT_OPTIONS', // [AGENT_OPTIONS:<kind>:<uuid>]{json}[/AGENT_OPTIONS]
  DISCOVERY_RESULTS: 'DISCOVERY_RESULTS' // [DISCOVERY_RESULTS:<uuid>]{json}[/DISCOVERY_RESULTS]
};

/**
 * SHIPPED (TD-2281 / TD-2442, sprint-39): bare reveal-loop poll markers (no
 * params). `BUSY` replaces the shared `[BUSY]` for reveal polls; `RESULT` is
 * prepended to every terminal reveal turn. Kept as full bracketed literals for
 * direct `includes()` checks in the poll coordinators / strip logic.
 */
export const REVEAL_MARKER = {
  BUSY: '[REVEAL_BUSY]',
  RESULT: '[REVEAL_RESULT]'
};

/**
 * FE → agent hidden control-reply prefixes (SHIPPED — defined in
 * td-agent/events/reveal_confirm.py and routed in td_agent.py). Sent via
 * `sendControlReply`, never shown as a chat bubble. Option/role replies are
 * NOT here — those are ordinary visible messages.
 */
export const ENRICHMENT_REPLY_PREFIX = {
  CONFIRM_REVEAL: 'CONFIRM_REVEAL:', // CONFIRM_REVEAL:<token>
  CANCEL_REVEAL: 'CANCEL_REVEAL:', // CANCEL_REVEAL:<token>
  CHECK_REVEAL: 'CHECK_REVEAL_RESULTS:' // CHECK_REVEAL_RESULTS:<jobId>
};

/**
 * Confirmation-token TTL (informational): td-backend REVEAL_TOKEN_TTL_MS is
 * 3 minutes. The FE has no expiry timestamp (not in the tag) — expiry is
 * handled reactively by the backend 403 + the agent's apology turn; this
 * constant only drives an optional stale-card treatment.
 */
export const REVEAL_TOKEN_TTL_MS = 3 * 60 * 1000;

/** ~20s hidden poll cadence for CHECK_REVEAL_RESULTS (mirrors search polls). */
export const REVEAL_POLL_INTERVAL_MS = 20000;

/** Kinds the agent may use for an `[AGENT_OPTIONS:<kind>:...]` bar (PROPOSED). */
export const OPTIONS_KIND = {
  REVEAL_CHOICES: 'reveal_choices',
  ROLES: 'roles',
  NEXT_STEPS: 'next_steps',
  DISAMBIGUATION: 'disambiguation'
};
